import { createApi, retry } from '@reduxjs/toolkit/query/react';
import { assert, getImageFormData } from 'common/utils';
import { mapCommunityFromApi, mapCommunityToApi } from 'community/api/mappers';
import { mapUserRoleToLabel } from 'community/mappers';
import { BasicPaginationParams, PaginatedApiPayload, PaginatedApiPayloadV2 } from 'domain/Common';
import { CommunityId, communityIdFromApi } from 'domain/CommunityId';
import { OrganizationMember, OrganizationMemberApiPayload } from 'domain/Organization';
import { UserRole } from 'domain/UserProfile';
import { map } from 'lodash-es';
import { axiosBaseQuery, getApiBaseUrl, mapToApiParams, transformPaginatedApiPayload } from 'modules/api/utils';

import { mapOrganizationMemberFromApi } from './mappers';

import type {
  ArchiveLabelPayload,
  AssignCommunityLabelsPayload,
  CommunityListFilters,
  GetManagedOrganizationApiPayload,
  GetOrganizationMembersParams,
  GetRolesResponse,
  LabelColor,
  LabelSummary,
  OrganizationMemberFormData,
  RecolorLabelPayload,
  RemoveOrganizationMemberPayload,
  RenameLabelPayload,
  RestoreLabelPayload,
  TransferCommunityPayload,
  UploadCommunityLogoPayload,
  WithOrganizationId,
} from './types';
import type { GetCommunityMembers, GetCommunityMembersParams } from 'community/api/types';
import type {
  Community,
  CommunityApiPayload,
  CommunityFormValues,
  CommunityGuest,
  CommunityMember,
  UpdateCommunityData,
} from 'domain/Community';

export const organizationIdErrorMessage = 'Organization ID required';
export const communityIdErrorMessage = 'Community ID required';

export const organizationApi = createApi({
  reducerPath: 'organizationApi',
  tagTypes: [
    'COMMUNITIES',
    'COMMUNITY',
    'COMMUNITY_MEMBERS',
    'ORGANIZATION_MEMBERS',
    'ORGANIZATION_LABELS',
    'LABEL_SUMMARIES',
  ],
  baseQuery: retry(axiosBaseQuery(), { maxRetries: 0 }),
  endpoints: (builder) => ({
    getManagedOrganizations: builder.query<GetManagedOrganizationApiPayload, void>({
      query: () => ({
        url: '/organization/managed',
      }),
    }),
    getOrganizationMembers: builder.query<PaginatedApiPayloadV2<OrganizationMember>, GetOrganizationMembersParams>({
      providesTags: (result) =>
        result?.data
          ? result.data.map(({ userId }) => ({ type: 'ORGANIZATION_MEMBERS', id: userId }))
          : ['ORGANIZATION_MEMBERS'],
      query: ({ organizationId, searchPhrase, page, pageSize, sortBy, sortOrder }) => ({
        url: `/organization/${organizationId}/members`,
        params: {
          page,
          size: pageSize,
          sortParam: sortBy,
          sortDir: sortOrder,
          searchText: searchPhrase || undefined,
        },
      }),
      transformResponse: ({
        organizationMembers,
      }: {
        organizationMembers: PaginatedApiPayloadV2<OrganizationMemberApiPayload>;
      }) => ({
        data: organizationMembers.data.map(mapOrganizationMemberFromApi),
        totalCount: organizationMembers.totalCount,
      }),
    }),
    removeOrganizationMember: builder.mutation<void, RemoveOrganizationMemberPayload>({
      invalidatesTags: ['ORGANIZATION_MEMBERS'],
      query: ({ organizationId, userId }) => {
        assert(organizationId, organizationIdErrorMessage);
        return {
          url: `/organization/${organizationId}/remove-user`,
          method: 'DELETE',
          data: { userId },
        };
      },
    }),
    inviteOrganizationMember: builder.mutation<void, OrganizationMemberFormData>({
      query: ({ organizationId, email, role, communityIds }) => {
        assert(organizationId, organizationIdErrorMessage);
        return {
          url: `/organization/${organizationId}/invite-user`,
          method: 'POST',
          data: { email, role, communityIds },
        };
      },
    }),
    updateOrganizationMemberAccess: builder.mutation<void, OrganizationMemberFormData>({
      invalidatesTags: (_result, _error, { userId }) => [{ type: 'ORGANIZATION_MEMBERS', id: userId }],
      query: ({ organizationId, userId, role, communityIds }) => {
        assert(organizationId, organizationIdErrorMessage);
        return {
          url: `/organization/${organizationId}/update-access`,
          method: 'POST',
          data: { userId, role, communityIds },
        };
      },
    }),
    joinOrganization: builder.mutation<void, WithOrganizationId>({
      query: ({ organizationId }) => {
        assert(organizationId, organizationIdErrorMessage);
        return {
          url: `/organization/${organizationId}/join`,
          method: 'POST',
        };
      },
    }),
    getCommunitiesList: builder.query<
      PaginatedApiPayload<Community>,
      WithOrganizationId<BasicPaginationParams<CommunityListFilters>>
    >({
      providesTags: (result) =>
        result?.data.length ? result.data.map(({ id }) => ({ type: 'COMMUNITIES', id })) : ['COMMUNITIES'],
      query: ({ organizationId, searchPhrase, page, pageSize, sortBy, sortOrder, filters }) => {
        assert(organizationId, organizationIdErrorMessage);
        return {
          url: `/organization/${organizationId}/communities`,
          params: {
            keywords: searchPhrase,
            page,
            size: pageSize,
            sortParam: sortBy,
            sortDir: sortOrder,
            // Raw-array repeatable query param (mirrors the roleIds precedent) -> binds to the backend List<String>.
            // Org-admin-only: ignored server-side for non-admin members.
            labels: filters?.labels,
          },
        };
      },
      // Not mapCommunityFromApi: the backend's CommunityListElement omits communitySettings, and the
      // shared mapper substitutes a zero-limit object for it, which would read as real settings here.
      transformResponse: (response: PaginatedApiPayload<CommunityApiPayload>): PaginatedApiPayload<Community> => {
        const payload = transformPaginatedApiPayload(response);
        const communities = (payload.data ?? []).map((community) => ({
          ...community,
          id: communityIdFromApi(community.id),
          // CommunityApiPayload carries these as `| null`; Community as `| undefined`.
          logoImageLink: community.logoImageLink ?? undefined,
          logoImageLinkSmall: community.logoImageLinkSmall ?? undefined,
          brandingColor: community.brandingColor ?? undefined,
        }));

        return { ...payload, content: communities, data: communities };
      },
    }),
    getCommunityById: builder.query<Community, { communityId: CommunityId }>({
      providesTags: (result) => (result ? [{ type: 'COMMUNITY', id: result.id }] : []),
      query: ({ communityId }) => ({
        url: `/community/${communityId}`,
      }),
      transformResponse: mapCommunityFromApi,
    }),
    getRoles: builder.query<UserRole[], void>({
      query: () => ({
        url: `/community/roles`,
      }),
      transformResponse: (result: GetRolesResponse) =>
        result.communityRoles.map((role) => ({ ...role, label: mapUserRoleToLabel(role.name) })),
    }),
    transferCommunity: builder.mutation<void, TransferCommunityPayload>({
      invalidatesTags: (_result, error, { communityId }) =>
        error ? [] : ['COMMUNITIES', { type: 'COMMUNITY', id: communityId }],
      query: ({ organizationId, communityId, organizationOwnerEmail }) => {
        assert(organizationId, organizationIdErrorMessage);
        return {
          url: `/organization/${organizationId}/transfer`,
          method: 'PATCH',
          data: {
            organizationOwnerEmail,
            communityId,
          },
        };
      },
    }),
    createCommunity: builder.mutation<Community, WithOrganizationId<{ values: CommunityFormValues }>>({
      invalidatesTags: ['COMMUNITIES'],
      query: ({ organizationId, values }) => {
        assert(organizationId, organizationIdErrorMessage);
        return {
          url: `/organization/${organizationId}/community`,
          method: 'POST',
          data: mapCommunityToApi(values),
        };
      },
      transformResponse: mapCommunityFromApi,
    }),
    updateCommunity: builder.mutation<Community, UpdateCommunityData>({
      invalidatesTags: (_result, _error, arg) => [
        { type: 'COMMUNITIES', id: arg.id },
        { type: 'COMMUNITY', id: arg.id },
      ],
      query: (data) => ({
        url: `/community/${data.id}`,
        method: 'PUT',
        data: mapCommunityToApi(data),
      }),
    }),
    uploadCommunityLogo: builder.mutation<
      Pick<Community, 'logoImageLink' | 'logoImageLinkSmall'>,
      UploadCommunityLogoPayload
    >({
      invalidatesTags: (_result, _error, { communityId }) =>
        _error
          ? []
          : [
              { type: 'COMMUNITIES', id: communityId },
              { type: 'COMMUNITY', id: communityId },
            ],
      query: ({ communityId, logo }) => ({
        url: `/community/${communityId}/uploadLogo`,
        method: 'POST',
        data: getImageFormData(logo),
      }),
    }),
    deleteCommunity: builder.mutation<void, WithOrganizationId<{ communityId: CommunityId }>>({
      invalidatesTags: (_result, _error, { communityId }) =>
        _error ? [] : ['COMMUNITIES', { type: 'COMMUNITY', id: communityId }],
      query: async ({ organizationId, communityId }) => {
        assert(organizationId, organizationIdErrorMessage);
        return {
          url: `/organization/${organizationId}/community/${communityId}`,
          method: 'DELETE',
        };
      },
    }),
    getCommunityMembers: builder.query<PaginatedApiPayload<CommunityMember>, GetCommunityMembersParams>({
      providesTags: (result) => (result?.data ? result.data.map(({ id }) => ({ type: 'COMMUNITY_MEMBERS', id })) : []),
      query: ({ page, pageSize, sortBy, sortOrder, showAdmins, tagFilters, searchPhrase, communityId, roles }) => {
        const hasTagFilters = tagFilters && Object.values(tagFilters).some((v) => v.length > 0);
        return {
          baseURL: getApiBaseUrl('v2'),
          url: `/community/${communityId}/members`,
          params: mapToApiParams({
            page,
            size: pageSize,
            sortParam: sortBy,
            sortDir: sortOrder,
            displayOnlyManagers: showAdmins,
            includeCallingUser: true,
            tagFilters: hasTagFilters ? JSON.stringify(tagFilters) : undefined,
            roleIds: map(roles, 'id'),
            searchText: searchPhrase.length ? searchPhrase : undefined,
          }) as GetCommunityMembers,
        };
      },
      transformResponse: transformPaginatedApiPayload,
    }),
    removeCommunityMember: builder.mutation<void, { memberId: number; communityId: CommunityId | undefined }>({
      query: ({ communityId, memberId }) => {
        assert(communityId, communityIdErrorMessage);
        return {
          url: `/community/${communityId}/${memberId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['COMMUNITY_MEMBERS'],
    }),
    inviteGuestsToCommunity: builder.mutation<void, { guests: CommunityGuest[]; communityId: CommunityId | undefined }>(
      {
        query: ({ communityId, guests }) => {
          assert(communityId, communityIdErrorMessage);
          return {
            url: `/community/${communityId}/members`,
            method: 'PUT',
            data: guests,
          };
        },
        invalidatesTags: ['COMMUNITY_MEMBERS'],
      }
    ),
    getOrganizationLabels: builder.query<string[], WithOrganizationId>({
      providesTags: ['ORGANIZATION_LABELS'],
      query: ({ organizationId }) => {
        assert(organizationId, organizationIdErrorMessage);
        return {
          url: `/organization/${organizationId}/labels`,
        };
      },
    }),
    getLabelSummaries: builder.query<LabelSummary[], WithOrganizationId>({
      providesTags: ['LABEL_SUMMARIES'],
      query: ({ organizationId }) => {
        assert(organizationId, organizationIdErrorMessage);
        return {
          url: `/organization/${organizationId}/labels/manage`,
          method: 'GET',
        };
      },
    }),
    getLabelColors: builder.query<LabelColor[], WithOrganizationId>({
      providesTags: ['ORGANIZATION_LABELS'],
      query: ({ organizationId }) => {
        assert(organizationId, organizationIdErrorMessage);
        return {
          url: `/organization/${organizationId}/labels/colors`,
          method: 'GET',
        };
      },
    }),
    assignCommunityLabels: builder.mutation<void, AssignCommunityLabelsPayload>({
      invalidatesTags: (_result, _error, { communityId }) =>
        _error ? [] : ['COMMUNITIES', 'ORGANIZATION_LABELS', { type: 'COMMUNITY', id: communityId }],
      query: ({ organizationId, communityId, labels }) => {
        assert(organizationId, organizationIdErrorMessage);
        return {
          url: `/organization/${organizationId}/community/${communityId}/labels`,
          method: 'PUT',
          data: { labels },
        };
      },
    }),
    recolorLabel: builder.mutation<void, RecolorLabelPayload>({
      invalidatesTags: (_result, _error) => (_error ? [] : ['LABEL_SUMMARIES', 'ORGANIZATION_LABELS', 'COMMUNITIES']),
      query: ({ organizationId, label, color }) => {
        assert(organizationId, organizationIdErrorMessage);
        return {
          url: `/organization/${organizationId}/labels/color`,
          method: 'PUT',
          data: { label, color },
        };
      },
    }),
    archiveLabel: builder.mutation<void, ArchiveLabelPayload>({
      invalidatesTags: ['LABEL_SUMMARIES', 'ORGANIZATION_LABELS', 'COMMUNITIES'],
      query: ({ organizationId, label }) => {
        assert(organizationId, organizationIdErrorMessage);
        return {
          url: `/organization/${organizationId}/labels/archive`,
          method: 'POST',
          data: { label },
        };
      },
    }),
    restoreLabel: builder.mutation<void, RestoreLabelPayload>({
      invalidatesTags: ['LABEL_SUMMARIES', 'ORGANIZATION_LABELS', 'COMMUNITIES'],
      query: ({ organizationId, label }) => {
        assert(organizationId, organizationIdErrorMessage);
        return {
          url: `/organization/${organizationId}/labels/restore`,
          method: 'POST',
          data: { label },
        };
      },
    }),
    renameLabel: builder.mutation<void, RenameLabelPayload>({
      // 409 (target archived) is an expected non-mutating path; don't refetch on error.
      invalidatesTags: (_result, _error) => (_error ? [] : ['LABEL_SUMMARIES', 'ORGANIZATION_LABELS', 'COMMUNITIES']),
      query: ({ organizationId, label, newLabel }) => {
        assert(organizationId, organizationIdErrorMessage);
        return {
          url: `/organization/${organizationId}/labels/rename`,
          method: 'POST',
          data: { label, newLabel },
        };
      },
    }),
  }),
});

export const {
  useCreateCommunityMutation,
  useGetOrganizationMembersQuery,
  useRemoveOrganizationMemberMutation,
  useGetCommunitiesListQuery,
  useLazyGetCommunitiesListQuery,
  useGetCommunityByIdQuery,
  useGetRolesQuery,
  useTransferCommunityMutation,
  useDeleteCommunityMutation,
  useGetManagedOrganizationsQuery,
  useUpdateCommunityMutation,
  useUploadCommunityLogoMutation,
  useGetCommunityMembersQuery,
  useRemoveCommunityMemberMutation,
  useInviteGuestsToCommunityMutation,
  useInviteOrganizationMemberMutation,
  useUpdateOrganizationMemberAccessMutation,
  useJoinOrganizationMutation,
  useGetOrganizationLabelsQuery,
  useGetLabelSummariesQuery,
  useGetLabelColorsQuery,
  useAssignCommunityLabelsMutation,
  useRecolorLabelMutation,
  useArchiveLabelMutation,
  useRestoreLabelMutation,
  useRenameLabelMutation,
} = organizationApi;
